import { IReflektionFacet, IReflektionFacetValue } from '../../../../services/rfk/types'
import { processPriceRangeFacet } from '../../../../services/rfk/util'
import type { IRogue } from '@/js/services/RogueTypes'

interface IFacetValues {
  [key: string]: IReflektionFacetValue;
}

export interface IPartialPublicProduct {
  name: string;
  attributes: {
    [attributeCode: string]: any;
  };
  inStock?: boolean;
  inventory?: { max: number };
  productId: number;
  primaryCategoryId: number;
  breadcrumbs?: string;
  url: string;
  price?: number;
   
  special_price?: number | string;
  thumbnail?: string;
}

const PRICE_RANGES: [string, number, number][] = [
  ['Under $50', -Infinity, 50],
  ['$50 - $100', 50, 100],
  ['$100 - $200', 100, 200],
  ['$200 - $300', 200, 300],
  ['$300 - $400', 300, 400],
  ['$400 - $500', 400, 500],
  ['Over $500', 500, Infinity],
]

const DISPLAY_NAMES: {[key: string] : string} = {
  pb            : 'Price Range',
  promo_status  : 'Hot Deal Status',
  colorstandard : 'Color',
  shirtsize     : 'Size',
  shoesize      : 'Shoe Size',
  shoetype      : 'Shoe Type',
  accessorytype : 'Accessory Type',
}

// Facet keys whose values are sizes, which are meaningless in count order
const SIZE_FACET_KEYS = ['shirtsize', 'shoesize']

const LETTER_SIZE_ORDER = ['XXXS', '3XS', 'XXS', '2XS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', 'XXXL', '3XL', '4XL', '5XL']

/**
 * Facet keys that read an attribute of a different name, in priority order.
 * The URL keys match the ones the category pages use so deals links stay
 * interchangeable with category links (and map cleanly onto Constructor later).
 */
const FACET_KEY_ATTRIBUTES: {[key: string]: string[]} = {
  colorstandard : ['color_standard', 'shirt_color'],
  // `generic_size` is a thin fallback — the deals feed only carries it for single-size child
  // SKUs, so it covers a fraction of apparel deals. Drop it here to hide the facet until the
  // feed exposes the sizes of configurable products.
  shirtsize     : ['shirt_size', 'generic_size'],
  shoesize      : ['shoe_size', 'shoesize'],
  shoetype      : ['shoe_type', 'shoetype'],
  accessorytype : ['accessory_type', 'accessory_type2'],
  compatibility : ['compatibility'],
}

const SHOE_TYPE_ATTRIBUTES = FACET_KEY_ATTRIBUTES.shoetype

const VALUE_NAME: {[key: string]: string} = {
  rogue_promo_enabled     : 'Hot Deals',
  rogue_closeout_and_sale : 'Closeout & Sale',
  rogue_hundo_enabled     : 'Hundo Pricing',
  rogue_boneyard          : 'Boneyard',
  promo_status            : 'Status',
  free_3                  : '3 Ships Free',
  black_friday_dropship   : '5 for 5',
}

function undoSnake(val: string = '') {
  return val.split('_').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ')
}

/**
 * Builds the root facet object with default values
 */
function buildFacetObject(facetKey: string): IReflektionFacet {
  return {
    key                : facetKey,
    display_name       : DISPLAY_NAMES[facetKey] || undoSnake(facetKey),
    number_of_products : 0,
    total              : 0,
    value              : [] as IReflektionFacetValue[],
  }
}

/**
 * Builds a facet value object from the facet string and sets default values
 */
function buildFacetValueObject(attributeValue: string): IReflektionFacetValue {
  const text = VALUE_NAME[attributeValue] || undoSnake(attributeValue)

  return {
    count  : 0,
    id     : `rogue_facet_id${Math.random().toString(36).substr(2)}`,
    text,
    urlKey : attributeValue,
    label  : text,
  }
}

/**
 * Creates the facet value, if needed, and increments the count
 */
function updateFacetValues(facetValues: IFacetValues, attributeValue: string, add: number = 1) {
  const fct = facetValues[attributeValue] || buildFacetValueObject(attributeValue)
  fct.count += add
  fct.label = `${fct.text} <span class="count">(${fct.count})</span>`
  facetValues[attributeValue] = fct
}

/**
 * Creates facet values for `is_saleable`
 */
// function isSaleableFcn(product: IPartialPublicProduct, facetValues: IFacetValues) {
//   const attributeValue = (product.inStock === true || (product.inStock === undefined && product.inventory?.max > 0)) ? 'In Stock' : 'Out of Stock'
//   updateFacetValues(facetValues, attributeValue)
// }

/**
 * Creates facet values for `pb` (price range)
 */
function pbFcn(product: IPartialPublicProduct, facetValues: IFacetValues) {
  const price = Number(product.attributes.promo_price || product.special_price || product.attributes?.special_price || product.price)
  if (typeof price === 'number' && !Number.isNaN(price)) {
    const range = PRICE_RANGES.find(([, lower, upper]) => price >= lower && price < upper)
    const r = range?.[0]
    if (r) {
      updateFacetValues(facetValues, r)
    }
  }
}

/**
 * Creates facet values for any keys that are attributes of the product.
 * This is the default/fallback method
 */
function attributesFcn(product: IPartialPublicProduct, facetValues: IFacetValues, facetKey: string) {
  // If the attribute is simply a key of the product.atribute object use it.
  // It may be an array, string or boolean, though. So handle those
  let attributeValue = product.attributes[facetKey]
  if (Array.isArray(attributeValue)) attributeValue = attributeValue[0]

  // If an attributeValue is found, construct the facetValueObject if needed
  // and increment the count
  if (typeof attributeValue === 'string') {
    updateFacetValues(facetValues, attributeValue)
  }
}

/**
 * Checks whether the product has an attribute value of `true` for a list of attribtue names.
 */
function booleanAttributesFcn(attributes: string[], product: IPartialPublicProduct, facetValues: IFacetValues) {
  attributes.forEach(attributeValue => {
    if (product.attributes[attributeValue] === true) {
      updateFacetValues(facetValues, attributeValue)
    }
  })
}

/**
 * Creates facet values for a facet key that doesn't match the attribute code it reads
 * (`colorstandard` reads `color_standard`, `shirtsize` reads `generic_size`, ...), taking
 * the first of `attributeCodes` the product actually has.
 *
 * Unlike `attributesFcn` this counts every value of a multi-valued attribute rather than
 * just the first, so a shirt offered in Black and Red shows up under both colors.
 */
function aliasedAttributesFcn(attributeCodes: string[], product: IPartialPublicProduct, facetValues: IFacetValues) {
  const code = attributeCodes.find(attributeCode => {
    const val = product.attributes[attributeCode]

    return val !== undefined && val !== null && val !== ''
  })

  if (!code) {
    return
  }

  const attributeValue = product.attributes[code]
  const values = (Array.isArray(attributeValue) ? attributeValue : [attributeValue])
    .filter(val => typeof val === 'string' || typeof val === 'number')
    .map(val => `${val}`.trim())
    .filter(val => val !== '')

  new Set(values).forEach(val => updateFacetValues(facetValues, val))
}

function promoStatusFcn(product: IPartialPublicProduct, facetValues: IFacetValues) {
  attributesFcn(product, facetValues, 'promo_status')
}
/**
 * This maps facet keys to their processing function above
 */
interface FacetFnMap {
  [key: string]: (prod: IPartialPublicProduct, vals: IFacetValues, key: string) => void
}

const FACET_KEY_FCN_MAP: FacetFnMap = {
  default      : attributesFcn,
  // is_saleable   : isSaleableFcn,
  promotions   : booleanAttributesFcn.bind(undefined, ['made_in_usa', '3_ships_free', 'free_shipping', 'rogue_closeout_and_sale', 'rogue_hundo_enabled']),
  promo_status : promoStatusFcn,
  shipping     : booleanAttributesFcn.bind(undefined, ['free_3', 'free_shipping']),
  deal_type    : booleanAttributesFcn.bind(undefined, ['rogue_boneyard', 'rogue_promo_enabled', 'rogue_closeout_and_sale', 'rogue_hundo_enabled']),
  pb           : pbFcn,
  ...Object.fromEntries(
    Object.entries(FACET_KEY_ATTRIBUTES)
      .map(([facetKey, attributeCodes]) => [facetKey, aliasedAttributesFcn.bind(undefined, attributeCodes)])
  ),
}

/**
 * Sorts size values the way a customer expects to read them: letter sizes smallest to
 * largest, then numeric sizes ascending, then anything else (`Single`, `One Size`, ...)
 * alphabetically at the end.
 */
function compareSizes(a: string, b: string): number {
  const rank = (val: string): [number, number] => {
    const letterIndex = LETTER_SIZE_ORDER.indexOf(val.toUpperCase())
    if (letterIndex !== -1) {
      return [0, letterIndex]
    }

    const numeric = parseFloat(val)

    return Number.isNaN(numeric) ? [2, 0] : [1, numeric]
  }

  const [aGroup, aValue] = rank(a)
  const [bGroup, bValue] = rank(b)

  return (aGroup - bGroup) || (aValue - bValue) || a.localeCompare(b)
}

/**
 * svc_content only sets `attributes.category` when the visible product itself has a
 * primary category. Hidden simples swapped to their visible parent (most apparel deals)
 * lose it, because the parent has no primary category and the response returns the
 * child's attributes. The root-level `breadcrumbs` field is always populated from the
 * child as a fallback, so derive the top-level category from it for the category facet.
 */
export function backfillCategoryAttribute(products: IPartialPublicProduct[]) {
  products.forEach(p => {
    if (!p.attributes.category && p.breadcrumbs) {
      p.attributes.category = p.breadcrumbs.split('>')[0].trim()
    }
  })
}

/**
 * The deals feed doesn't carry a shoe type attribute, but every shoe's breadcrumb trail
 * names it (`Shoes > Weightlifting Shoes > TYR`), so derive it from the second segment.
 * Run this after `backfillCategoryAttribute` — it keys off the category attribute. A real
 * attribute on the feed always wins, so this becomes a no-op if one is ever added.
 */
export function backfillShoeTypeAttribute(products: IPartialPublicProduct[]) {
  products.forEach(p => {
    if (p.attributes.category !== 'Shoes') {
      return
    }

    if (SHOE_TYPE_ATTRIBUTES.some(code => p.attributes[code])) {
      return
    }

    const shoeType = (p.breadcrumbs || '').split('>')[1]?.trim()
    if (shoeType) {
      p.attributes.shoe_type = shoeType
    }
  })
}

/**
 * Builds a custom IReflektionFacet[] array from a set of entityIds and facet keys.
 * This is used to create a set of facets (filters) on category pages without Reflektion.
 */
export default function buildFacets(
  facetKeys: string[],
  products: IPartialPublicProduct[],
  rogue: IRogue
): IReflektionFacet[] {
  const facetObjects: IReflektionFacet[] = facetKeys.map(buildFacetObject)
  facetObjects.forEach(facet => {
    const facetValues: IFacetValues = {}
    let fcn = FACET_KEY_FCN_MAP[facet.key] || FACET_KEY_FCN_MAP.default
    if (facet.key === 'deal_type' && rogue) {
      if (rogue.featureFlags.isOn('fiveforfive_enabled')) {
        fcn = booleanAttributesFcn.bind(undefined, ['rogue_boneyard', 'rogue_promo_enabled', 'rogue_closeout_and_sale', 'rogue_hundo_enabled', 'black_friday_dropship'])
      }
    }

    products.forEach(product => fcn(product, facetValues, facet.key))

    facet.value = Object.keys(facetValues)
      .map(key => {
        if (facet.key === 'pb' && rogue) {
          facetValues[key].text = processPriceRangeFacet(facetValues[key].text, rogue)
        }

        return facetValues[key]
      })
      .sort((a, b) => {
        if (facet.key === 'pb') {
          return PRICE_RANGES.findIndex(range => range[0] === a.urlKey)
            - PRICE_RANGES.findIndex(range => range[0] === b.urlKey)
        }

        if (SIZE_FACET_KEYS.indexOf(facet.key) !== -1) {
          return compareSizes(a.urlKey, b.urlKey)
        }

        return b.count - a.count || a.text.localeCompare(b.text)
      })
    facet.total = facet.value.length
    facet.number_of_products = facet.value.reduce((reducer, v) => reducer + v.count, 0)
  })

  return facetObjects.filter(obj => obj.value.length > 0)
}
